import React, { useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './FundingSheet.scss';

const FundingSheet = ({ user, isPublic }) => {
  console.log('FundingSheet rendered:', { user, isPublic });
  
  // Redirect if not authorized (admin view only)
  useEffect(() => {
    console.log('FundingSheet useEffect:', { isPublic, userEmail: user?.email });
    if (!isPublic) {
      // Admin view - require chris@siki.io
      if (user?.email !== 'chris@siki.io') {
        console.log('Redirecting to /admin - not authorized');
        window.location.href = '/admin';
      }
    }
    // For public view, token validation is handled by PublicFundingSheet component
  }, [user, isPublic]);

  // State for budget data
  const [budgetData] = useState({
    totalBudget: 5000000, // Updated to reflect actual funding needs
    year1: {
      infrastructure: {
        awsHosting: { q1: 2500, q2: 5000, q3: 12000, q4: 20000 },
        agoraStreaming: { q1: 3000, q2: 7000, q3: 20000, q4: 40000 },
        redisCache: { q1: 800, q2: 1500, q3: 3000, q4: 5000 },
        mongoDatabase: { q1: 1200, q2: 2000, q3: 5000, q4: 8000 },
        cdnCloudfront: { q1: 1000, q2: 2000, q3: 4000, q4: 7000 },
        monitoring: { q1: 500, q2: 750, q3: 1000, q4: 1500 }
      },
      leadership: {
        ceo: { monthly: 15000, months: 12 },
        coo: { monthly: 10000, months: 12 },
        cfo: { monthly: 10000, months: 12 },
        cmo: { monthly: 10000, months: 12 },
        cao: { monthly: 10000, months: 12 },
        businessDev: { monthly: 8333, months: 12 },
        copyrightCoordinator: { monthly: 10000, months: 12 },
        projectManager: { monthly: 8000, months: 12 },
        hrDirector: { monthly: 7000, months: 12 }
      },
      development: {
        backendDev: { monthly: 10000, months: 12, count: 1 },
        mobileDev: { monthly: 10000, months: 12 },
        frontendDev: { monthly: 10000, months: 12 },
        devOps: { monthly: 8000, months: 12 },
        qaDev: { monthly: 7500, months: 12, count: 2 },
        mlEngineer: { monthly: 20000, months: 12 },
        tester: { monthly: 3000, months: 12, count: 4 },
        dataEngineer: { monthly: 15000, months: 12 },
        securityEngineer: { monthly: 15000, months: 12 },
        platformEngineer: { monthly: 15000, months: 12 },
        blockchainEngineer: { monthly: 18000, months: 12 },
        uxuiDesigner: { monthly: 7500, months: 12 },
        graphicDesigner: { monthly: 7500, months: 12 }
      },
      sales: {
        salesLead: { monthly: 8333, months: 12 },
        salesRep1: { monthly: 5000, months: 12 },
        salesRep2: { monthly: 5000, months: 12 }
      },
      influencerProgram: {
        q1: {
          tierA: { monthlyPerInfluencer: 50000, months: 3, count: 1 },
          tierB: { monthlyPerInfluencer: 3000, months: 3, count: 2 },
          tierC: { monthlyPerInfluencer: 1000, months: 3, count: 5 },
          influencerManager: { monthly: 10000, months: 3 },
          influencerAssistant: { monthly: 5000, months: 3 }
        },
        q2: {
          tierA: { monthlyPerInfluencer: 50000, months: 3, count: 1 },
          tierB: { monthlyPerInfluencer: 3000, months: 3, count: 3 },
          tierC: { monthlyPerInfluencer: 1000, months: 3, count: 8 },
          influencerManager: { monthly: 10000, months: 3 },
          influencerAssistant: { monthly: 5000, months: 3 }
        },
        q3: {
          tierA: { monthlyPerInfluencer: 50000, months: 3, count: 2 },
          tierB: { monthlyPerInfluencer: 3000, months: 3, count: 5 },
          tierC: { monthlyPerInfluencer: 1000, months: 3, count: 10 },
          influencerManager: { monthly: 10000, months: 3 },
          influencerAssistant: { monthly: 5000, months: 3 }
        },
        q4: {
          tierA: { monthlyPerInfluencer: 50000, months: 3, count: 2 },
          tierB: { monthlyPerInfluencer: 3000, months: 3, count: 5 },
          tierC: { monthlyPerInfluencer: 1000, months: 3, count: 10 },
          influencerManager: { monthly: 10000, months: 3 },
          influencerAssistant: { monthly: 5000, months: 3 }
        }
      },
      prizesRewards: {
        monthlyPrizes: { monthly: 3000, months: 12 },
        quarterlyGrandPrizes: { quarterly: 10000, quarters: 4 },
        yearEndMegaPrize: { total: 20000 },
        userIncentives: { monthly: 333, months: 12 }
      },
      operations: {
        supportTeam: { monthly: 3333, count: 2, months: 12 },
        legal: { monthly: 10000, months: 12 },
        accounting: { monthly: 2000, months: 12 },
        moderatorClipper: { monthly: 5000, count: 5, months: 12 }
      },
      customerSupport: {
        trustSafety: { monthly: 5000, count: 1, months: 12 }
      },
      marketing: {
        digitalAds: { q1: 25000, q2: 50000, q3: 75000, q4: 75000 }, // Capped at $25K/month
        prCommunications: { monthly: 10000, months: 12 },
        brandMarketing: { quarterly: 15000, quarters: 4 },
        socialMediaManager: { monthly: 7500, months: 12 }
      },
      compliance: {
        generalLiability: { annual: 12000, count: 1 },
        cyberInsurance: { annual: 15000, count: 1 },
        musicLicensing: { annual: 25000, count: 1 }, // ASCAP, BMI, SESAC, etc.
        dataPrivacy: { quarterly: 10000, quarters: 4 } // GDPR, CCPA compliance
      },
      office: {
        rent: { monthly: 7500, months: 12 },
        utilities: { monthly: 1600, months: 12 },
        equipment: { quarterly: 20000, quarters: 1 }, // $20,000 total for equipment
        remoteStipends: { monthly: 150, count: 20, months: 12 } // For remote team
      },
      travelAndHotel: {
        teamMeetings: { quarterly: 25000, quarters: 4 }, // Quarterly all-hands meetings
        clientMeetings: { monthly: 5000, months: 12 }, // Client/partner meetings
        conferences: { quarterly: 15000, quarters: 4 }, // Industry conferences
        influencerEvents: { quarterly: 30000, quarters: 4 }, // Influencer meetups/events
        roadshows: { quarterly: 20000, quarters: 4 }, // Investor/marketing roadshows
        executiveTravel: { monthly: 8000, months: 12 }, // Executive team travel
        internationalExpansion: { quarterly: 10000, quarters: 4 } // International market research
      },
      professionalServices: {
        externalLegal: { monthly: 10000, months: 12 },
        accounting: { quarterly: 15000, quarters: 4 }
      },
      musicRoyalties: {
        // Streaming royalty costs
        royaltyPerStream: 0.006, // $0.006 per stream
        freeUserStreamsPerMonth: 10, // Occasional on-demand listening
        subscriberStreamsPerMonth: 30, // Some on-demand, mostly live streams
        // Live streaming multiplier - pays per listener
        liveStreamMultiplier: 1, // 1x per user in the stream
        avgSongsPerLiveStream: 15, // Average songs played in a 1-hour stream
        // Stream size distribution (more realistic with viral streams)
        streamDistribution: {
          small: { viewers: 10, percentage: 0.70 }, // 70% have 10 viewers
          medium: { viewers: 50, percentage: 0.20 }, // 20% have 50 viewers
          large: { viewers: 500, percentage: 0.08 }, // 8% have 500 viewers
          viral: { viewers: 5000, percentage: 0.02 } // 2% go viral with 5K+ viewers
        },
        q1LiveStreamsPerDay: 50, // ~1 per 1000 users
        q2LiveStreamsPerDay: 250, // ~1 per 1000 users
        q3LiveStreamsPerDay: 1000, // ~1 per 1000 users
        q4LiveStreamsPerDay: 2000 // ~1 per 1000 users
      },
      revenue: {
        tips: { 
          // More realistic: Only 5% of users tip, averaging $10/month per tipper
          q1TippingUsers: 0.02, q2TippingUsers: 0.03, q3TippingUsers: 0.04, q4TippingUsers: 0.05,
          q1AvgPerTipper: 8, q2AvgPerTipper: 10, q3AvgPerTipper: 12, q4AvgPerTipper: 15,
          platformCut: 0.25, // Average of 30% non-sub and 20% sub
          q1Users: 50000, q2Users: 250000, q3Users: 1000000, q4Users: 2000000
        },
        subscriptions: {
          monthlyPrice: 12.99,
          // More realistic conversion rates based on industry data (15-25% convert)
          q1Penetration: 0.02, q2Penetration: 0.03, q3Penetration: 0.04, q4Penetration: 0.05,
          q1Users: 50000, q2Users: 250000, q3Users: 1000000, q4Users: 2000000
        },
        collectibles: {
          // Only 1% of users buy collectibles, but they spend more
          q1BuyerPercentage: 0.005, q2BuyerPercentage: 0.008, q3BuyerPercentage: 0.01, q4BuyerPercentage: 0.012,
          q1AvgPerBuyer: 50, q2AvgPerBuyer: 60, q3AvgPerBuyer: 75, q4AvgPerBuyer: 100,
          platformCut: 0.03,
          q1Users: 50000, q2Users: 250000, q3Users: 1000000, q4Users: 2000000
        },
        songUploads: {
          pricePerUpload: 2,
          q1UploadsPerCreator: 1, q2UploadsPerCreator: 1.5, q3UploadsPerCreator: 2, q4UploadsPerCreator: 2.5,
          creatorPercentage: 0.02, // Only 2% of users are active creators
          q1Users: 50000, q2Users: 250000, q3Users: 1000000, q4Users: 2000000
        },
        adRevenue: {
          // On-demand streaming ads
          baseRPU: 0.12, // Base rate from on-demand streaming (1 ad per 3 songs)
          // Live streaming ads - much more valuable
          liveStreamCPM: 25, // Higher CPM for live stream ads
          adsPerLiveStream: 5, // 5 ads per hour-long stream
          // Using same live stream numbers from musicRoyalties
          platformCut: 0.35,
          q1Users: 50000, q2Users: 250000, q3Users: 1000000, q4Users: 2000000
        },
        studioCreation: {
          monthlyPrice: 19,
          // Studio adoption by creators (0.5-1% of user base)
          q1Studios: 250, q2Studios: 1250, q3Studios: 5000, q4Studios: 20000
        },
        creatorSubscriptions: {
          // Creator-to-fan subscriptions (like Patreon/OnlyFans model)
          // Math check: Q4 = 10K creators × 30 subs = 300K subscribers (15% of 2M users)
          platformCut: 0.30, // Platform takes 30%
          tiers: {
            basic: { price: 5, percentage: 0.60 }, // 60% of creator subs are $5
            standard: { price: 10, percentage: 0.30 }, // 30% are $10
            premium: { price: 20, percentage: 0.10 } // 10% are $20 (big creators)
          },
          // Percentage of users who are monetizing creators
          q1CreatorPercentage: 0.001, // 0.1% of users monetize in Q1 (50 creators)
          q2CreatorPercentage: 0.002, // 0.2% in Q2 (500 creators)
          q3CreatorPercentage: 0.003, // 0.3% in Q3 (3,000 creators)
          q4CreatorPercentage: 0.005, // 0.5% in Q4 (10,000 creators)
          // Average subscribers per monetizing creator
          q1AvgSubsPerCreator: 15,
          q2AvgSubsPerCreator: 20,
          q3AvgSubsPerCreator: 25,
          q4AvgSubsPerCreator: 30,
          q1Users: 50000, q2Users: 250000, q3Users: 1000000, q4Users: 2000000
        }
      }
    }
  });

  // Calculate totals
  const calculateCategoryTotal = (category) => {
    let total = 0;
    Object.keys(category).forEach(key => {
      const item = category[key];
      // Handle quarterly influencer program structure
      if (key.startsWith('q') && item.tierA) {
        // Calculate quarterly influencer costs
        let quarterTotal = 0;
        if (item.tierA) quarterTotal += item.tierA.monthlyPerInfluencer * item.tierA.months * item.tierA.count;
        if (item.tierB) quarterTotal += item.tierB.monthlyPerInfluencer * item.tierB.months * item.tierB.count;
        if (item.tierC) quarterTotal += item.tierC.monthlyPerInfluencer * item.tierC.months * item.tierC.count;
        if (item.influencerManager) quarterTotal += item.influencerManager.monthly * item.influencerManager.months;
        if (item.influencerAssistant) quarterTotal += item.influencerAssistant.monthly * item.influencerAssistant.months;
        total += quarterTotal;
      } else if (item.monthly && item.months) {
        total += item.monthly * item.months * (item.count || 1);
      } else if (item.monthlyPerInfluencer && item.months && item.count) {
        total += item.monthlyPerInfluencer * item.months * item.count;
      } else if (item.q1 !== undefined || item.q2 !== undefined || item.q3 !== undefined || item.q4 !== undefined) {
        // Quarterly amounts (not monthly) - sum them directly with defensive checks
        total += (item.q1 || 0) + (item.q2 || 0) + (item.q3 || 0) + (item.q4 || 0);
      } else if (item.quarterly && item.quarters) {
        total += item.quarterly * item.quarters;
      } else if (item.semiAnnual && item.count) {
        total += item.semiAnnual * item.count;
      } else if (item.annual && item.count) {
        total += item.annual * item.count;
      } else if (item.perHire && item.hires) {
        total += item.perHire * item.hires;
      } else if (item.total) {
        total += item.total;
      }
    });
    return total;
  };

  const totals = {
    infrastructure: calculateCategoryTotal(budgetData.year1.infrastructure),
    leadership: calculateCategoryTotal(budgetData.year1.leadership),
    development: calculateCategoryTotal(budgetData.year1.development),
    sales: calculateCategoryTotal(budgetData.year1.sales),
    influencerProgram: calculateCategoryTotal(budgetData.year1.influencerProgram),
    prizesRewards: calculateCategoryTotal(budgetData.year1.prizesRewards),
    operations: calculateCategoryTotal(budgetData.year1.operations),
    customerSupport: calculateCategoryTotal(budgetData.year1.customerSupport),
    marketing: calculateCategoryTotal(budgetData.year1.marketing),
    compliance: calculateCategoryTotal(budgetData.year1.compliance),
    office: calculateCategoryTotal(budgetData.year1.office),
    travelAndHotel: calculateCategoryTotal(budgetData.year1.travelAndHotel),
    professionalServices: calculateCategoryTotal(budgetData.year1.professionalServices)
  };

  // Calculate music royalty costs
  const calculateMusicRoyalties = () => {
    const royalties = budgetData.year1.musicRoyalties;
    const rev = budgetData.year1.revenue;
    
    // Calculate subscription penetration for each quarter
    const subsQ1 = rev.subscriptions.q1Users * rev.subscriptions.q1Penetration;
    const subsQ2 = rev.subscriptions.q2Users * rev.subscriptions.q2Penetration;
    const subsQ3 = rev.subscriptions.q3Users * rev.subscriptions.q3Penetration;
    const subsQ4 = rev.subscriptions.q4Users * rev.subscriptions.q4Penetration;
    
    // Calculate free users (total - subscribers)
    const freeQ1 = rev.subscriptions.q1Users - subsQ1;
    const freeQ2 = rev.subscriptions.q2Users - subsQ2;
    const freeQ3 = rev.subscriptions.q3Users - subsQ3;
    const freeQ4 = rev.subscriptions.q4Users - subsQ4;
    
    // On-demand streaming royalties (monthly streams * royalty rate * 3 months)
    const onDemandQ1 = ((freeQ1 * royalties.freeUserStreamsPerMonth) + (subsQ1 * royalties.subscriberStreamsPerMonth)) * royalties.royaltyPerStream * 3;
    const onDemandQ2 = ((freeQ2 * royalties.freeUserStreamsPerMonth) + (subsQ2 * royalties.subscriberStreamsPerMonth)) * royalties.royaltyPerStream * 3;
    const onDemandQ3 = ((freeQ3 * royalties.freeUserStreamsPerMonth) + (subsQ3 * royalties.subscriberStreamsPerMonth)) * royalties.royaltyPerStream * 3;
    const onDemandQ4 = ((freeQ4 * royalties.freeUserStreamsPerMonth) + (subsQ4 * royalties.subscriberStreamsPerMonth)) * royalties.royaltyPerStream * 3;
    
    // Calculate weighted average viewers based on stream distribution
    const dist = royalties.streamDistribution;
    const weightedAvgViewers = (dist.small.viewers * dist.small.percentage) + 
                               (dist.medium.viewers * dist.medium.percentage) + 
                               (dist.large.viewers * dist.large.percentage) + 
                               (dist.viral.viewers * dist.viral.percentage);
    
    // Live streaming royalties (streams per day * weighted avg listeners * songs per stream * royalty * 90 days)
    const liveQ1 = royalties.q1LiveStreamsPerDay * weightedAvgViewers * royalties.avgSongsPerLiveStream * royalties.royaltyPerStream * 90;
    const liveQ2 = royalties.q2LiveStreamsPerDay * weightedAvgViewers * royalties.avgSongsPerLiveStream * royalties.royaltyPerStream * 90;
    const liveQ3 = royalties.q3LiveStreamsPerDay * weightedAvgViewers * royalties.avgSongsPerLiveStream * royalties.royaltyPerStream * 90;
    const liveQ4 = royalties.q4LiveStreamsPerDay * weightedAvgViewers * royalties.avgSongsPerLiveStream * royalties.royaltyPerStream * 90;
    
    return {
      onDemand: { q1: onDemandQ1, q2: onDemandQ2, q3: onDemandQ3, q4: onDemandQ4, total: onDemandQ1 + onDemandQ2 + onDemandQ3 + onDemandQ4 },
      liveStreaming: { q1: liveQ1, q2: liveQ2, q3: liveQ3, q4: liveQ4, total: liveQ1 + liveQ2 + liveQ3 + liveQ4 },
      totalByQuarter: {
        q1: onDemandQ1 + liveQ1,
        q2: onDemandQ2 + liveQ2,
        q3: onDemandQ3 + liveQ3,
        q4: onDemandQ4 + liveQ4
      }
    };
  };

  const musicRoyaltyCosts = calculateMusicRoyalties();
  const totalMusicRoyalties = musicRoyaltyCosts.onDemand.total + musicRoyaltyCosts.liveStreaming.total;
  
  // Add music royalties to totals
  totals.musicRoyalties = totalMusicRoyalties;

  // Calculate revenue
  const calculateRevenueByQuarter = () => {
    const rev = budgetData.year1.revenue;
    
    // Tips revenue (tipping users * avg per tipper * platform cut * 3 months)
    const tipsQ1 = (rev.tips.q1Users * rev.tips.q1TippingUsers) * rev.tips.q1AvgPerTipper * rev.tips.platformCut * 3;
    const tipsQ2 = (rev.tips.q2Users * rev.tips.q2TippingUsers) * rev.tips.q2AvgPerTipper * rev.tips.platformCut * 3;
    const tipsQ3 = (rev.tips.q3Users * rev.tips.q3TippingUsers) * rev.tips.q3AvgPerTipper * rev.tips.platformCut * 3;
    const tipsQ4 = (rev.tips.q4Users * rev.tips.q4TippingUsers) * rev.tips.q4AvgPerTipper * rev.tips.platformCut * 3;
    
    // Subscription revenue
    const subsQ1 = rev.subscriptions.monthlyPrice * (rev.subscriptions.q1Users * rev.subscriptions.q1Penetration) * 3;
    const subsQ2 = rev.subscriptions.monthlyPrice * (rev.subscriptions.q2Users * rev.subscriptions.q2Penetration) * 3;
    const subsQ3 = rev.subscriptions.monthlyPrice * (rev.subscriptions.q3Users * rev.subscriptions.q3Penetration) * 3;
    const subsQ4 = rev.subscriptions.monthlyPrice * (rev.subscriptions.q4Users * rev.subscriptions.q4Penetration) * 3;
    
    // Collectibles revenue (buyers * avg per buyer * platform cut * 3 months)
    const collectQ1 = (rev.collectibles.q1Users * rev.collectibles.q1BuyerPercentage) * rev.collectibles.q1AvgPerBuyer * rev.collectibles.platformCut * 3;
    const collectQ2 = (rev.collectibles.q2Users * rev.collectibles.q2BuyerPercentage) * rev.collectibles.q2AvgPerBuyer * rev.collectibles.platformCut * 3;
    const collectQ3 = (rev.collectibles.q3Users * rev.collectibles.q3BuyerPercentage) * rev.collectibles.q3AvgPerBuyer * rev.collectibles.platformCut * 3;
    const collectQ4 = (rev.collectibles.q4Users * rev.collectibles.q4BuyerPercentage) * rev.collectibles.q4AvgPerBuyer * rev.collectibles.platformCut * 3;
    
    // Song uploads revenue
    const uploadsQ1 = rev.songUploads.pricePerUpload * rev.songUploads.q1UploadsPerCreator * (rev.songUploads.q1Users * rev.songUploads.creatorPercentage) * 3;
    const uploadsQ2 = rev.songUploads.pricePerUpload * rev.songUploads.q2UploadsPerCreator * (rev.songUploads.q2Users * rev.songUploads.creatorPercentage) * 3;
    const uploadsQ3 = rev.songUploads.pricePerUpload * rev.songUploads.q3UploadsPerCreator * (rev.songUploads.q3Users * rev.songUploads.creatorPercentage) * 3;
    const uploadsQ4 = rev.songUploads.pricePerUpload * rev.songUploads.q4UploadsPerCreator * (rev.songUploads.q4Users * rev.songUploads.creatorPercentage) * 3;
    
    // Ad revenue - combining on-demand and live stream ads
    const royalties = budgetData.year1.musicRoyalties;
    
    // On-demand ad revenue (base rate * users * 3 months)
    const onDemandAdsQ1 = rev.adRevenue.baseRPU * rev.adRevenue.q1Users * 3;
    const onDemandAdsQ2 = rev.adRevenue.baseRPU * rev.adRevenue.q2Users * 3;
    const onDemandAdsQ3 = rev.adRevenue.baseRPU * rev.adRevenue.q3Users * 3;
    const onDemandAdsQ4 = rev.adRevenue.baseRPU * rev.adRevenue.q4Users * 3;
    
    // Calculate weighted average viewers (same as for royalties)
    const dist = royalties.streamDistribution;
    const weightedAvgViewers = (dist.small.viewers * dist.small.percentage) + 
                               (dist.medium.viewers * dist.medium.percentage) + 
                               (dist.large.viewers * dist.large.percentage) + 
                               (dist.viral.viewers * dist.viral.percentage);
    
    // Live stream ad revenue (streams/day * weighted viewers * ads/stream * CPM/1000 * platform cut * 90 days)
    const liveAdsQ1 = royalties.q1LiveStreamsPerDay * weightedAvgViewers * rev.adRevenue.adsPerLiveStream * (rev.adRevenue.liveStreamCPM/1000) * rev.adRevenue.platformCut * 90;
    const liveAdsQ2 = royalties.q2LiveStreamsPerDay * weightedAvgViewers * rev.adRevenue.adsPerLiveStream * (rev.adRevenue.liveStreamCPM/1000) * rev.adRevenue.platformCut * 90;
    const liveAdsQ3 = royalties.q3LiveStreamsPerDay * weightedAvgViewers * rev.adRevenue.adsPerLiveStream * (rev.adRevenue.liveStreamCPM/1000) * rev.adRevenue.platformCut * 90;
    const liveAdsQ4 = royalties.q4LiveStreamsPerDay * weightedAvgViewers * rev.adRevenue.adsPerLiveStream * (rev.adRevenue.liveStreamCPM/1000) * rev.adRevenue.platformCut * 90;
    
    // Total ad revenue
    const adsQ1 = onDemandAdsQ1 + liveAdsQ1;
    const adsQ2 = onDemandAdsQ2 + liveAdsQ2;
    const adsQ3 = onDemandAdsQ3 + liveAdsQ3;
    const adsQ4 = onDemandAdsQ4 + liveAdsQ4;
    
    // Studio revenue
    const studioQ1 = rev.studioCreation.monthlyPrice * rev.studioCreation.q1Studios * 3;
    const studioQ2 = rev.studioCreation.monthlyPrice * rev.studioCreation.q2Studios * 3;
    const studioQ3 = rev.studioCreation.monthlyPrice * rev.studioCreation.q3Studios * 3;
    const studioQ4 = rev.studioCreation.monthlyPrice * rev.studioCreation.q4Studios * 3;
    
    // Creator subscription revenue
    const creatorSubs = rev.creatorSubscriptions;
    const tiers = creatorSubs.tiers;
    const weightedAvgPrice = (tiers.basic.price * tiers.basic.percentage) + 
                            (tiers.standard.price * tiers.standard.percentage) + 
                            (tiers.premium.price * tiers.premium.percentage);
    
    // Calculate creator subscription revenue (creators * avg subs * weighted price * platform cut * 3 months)
    const creatorSubsQ1 = (creatorSubs.q1Users * creatorSubs.q1CreatorPercentage) * creatorSubs.q1AvgSubsPerCreator * weightedAvgPrice * creatorSubs.platformCut * 3;
    const creatorSubsQ2 = (creatorSubs.q2Users * creatorSubs.q2CreatorPercentage) * creatorSubs.q2AvgSubsPerCreator * weightedAvgPrice * creatorSubs.platformCut * 3;
    const creatorSubsQ3 = (creatorSubs.q3Users * creatorSubs.q3CreatorPercentage) * creatorSubs.q3AvgSubsPerCreator * weightedAvgPrice * creatorSubs.platformCut * 3;
    const creatorSubsQ4 = (creatorSubs.q4Users * creatorSubs.q4CreatorPercentage) * creatorSubs.q4AvgSubsPerCreator * weightedAvgPrice * creatorSubs.platformCut * 3;
    
    return {
      tips: { q1: tipsQ1, q2: tipsQ2, q3: tipsQ3, q4: tipsQ4, total: tipsQ1 + tipsQ2 + tipsQ3 + tipsQ4 },
      subscriptions: { q1: subsQ1, q2: subsQ2, q3: subsQ3, q4: subsQ4, total: subsQ1 + subsQ2 + subsQ3 + subsQ4 },
      collectibles: { q1: collectQ1, q2: collectQ2, q3: collectQ3, q4: collectQ4, total: collectQ1 + collectQ2 + collectQ3 + collectQ4 },
      songUploads: { q1: uploadsQ1, q2: uploadsQ2, q3: uploadsQ3, q4: uploadsQ4, total: uploadsQ1 + uploadsQ2 + uploadsQ3 + uploadsQ4 },
      adRevenue: { q1: adsQ1, q2: adsQ2, q3: adsQ3, q4: adsQ4, total: adsQ1 + adsQ2 + adsQ3 + adsQ4 },
      studioCreation: { q1: studioQ1, q2: studioQ2, q3: studioQ3, q4: studioQ4, total: studioQ1 + studioQ2 + studioQ3 + studioQ4 },
      creatorSubscriptions: { q1: creatorSubsQ1, q2: creatorSubsQ2, q3: creatorSubsQ3, q4: creatorSubsQ4, total: creatorSubsQ1 + creatorSubsQ2 + creatorSubsQ3 + creatorSubsQ4 },
      totalByQuarter: {
        q1: tipsQ1 + subsQ1 + collectQ1 + uploadsQ1 + adsQ1 + studioQ1 + creatorSubsQ1,
        q2: tipsQ2 + subsQ2 + collectQ2 + uploadsQ2 + adsQ2 + studioQ2 + creatorSubsQ2,
        q3: tipsQ3 + subsQ3 + collectQ3 + uploadsQ3 + adsQ3 + studioQ3 + creatorSubsQ3,
        q4: tipsQ4 + subsQ4 + collectQ4 + uploadsQ4 + adsQ4 + studioQ4 + creatorSubsQ4
      }
    };
  };
  
  const revenueData = calculateRevenueByQuarter();
  const totalRevenue = revenueData.tips.total + revenueData.subscriptions.total + revenueData.collectibles.total + 
                      revenueData.songUploads.total + revenueData.adRevenue.total + revenueData.studioCreation.total + 
                      revenueData.creatorSubscriptions.total;
  
  
  // Calculate final totals
  const year1Total = Object.values(totals).reduce((sum, val) => sum + val, 0);
  const contingency = year1Total * 0.1;
  const grandTotal = year1Total + contingency;


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercent = (amount, total) => {
    return ((amount / total) * 100).toFixed(1) + '%';
  };

  // Calculate quarterly operating costs more accurately
  const calculateQuarterlyCosts = () => {
    // Fixed monthly costs that don't scale
    const fixedMonthly = (
      (totals.leadership || 0) / 12 +
      (totals.development || 0) / 12 +
      (totals.sales || 0) / 12 +
      (totals.operations || 0) / 12 +
      (totals.customerSupport || 0) / 12 +
      (totals.office || 0) / 12 +
      (totals.professionalServices || 0) / 12 +
      ((budgetData.year1.compliance?.dataPrivacy?.quarterly || 0) / 3) +
      (budgetData.year1.marketing?.prCommunications?.monthly || 0) +
      ((budgetData.year1.marketing?.brandMarketing?.quarterly || 0) / 3) +
      (budgetData.year1.prizesRewards?.monthlyPrizes?.monthly || 0)
    );

    // Q1 specific costs
    const q1Costs = (fixedMonthly * 3) +
      (budgetData.year1.infrastructure?.awsHosting?.q1 || 0) +
      (budgetData.year1.infrastructure?.agoraStreaming?.q1 || 0) +
      (budgetData.year1.infrastructure?.database?.q1 || 0) +
      (budgetData.year1.infrastructure?.cdn?.q1 || 0) +
      (budgetData.year1.infrastructure?.other?.q1 || 0) +
      (budgetData.year1.marketing?.digitalAds?.q1 || 0) +
      (((budgetData.year1.influencerProgram?.q1?.tierA?.monthlyPerInfluencer || 0) * (budgetData.year1.influencerProgram?.q1?.tierA?.count || 0)) +
       ((budgetData.year1.influencerProgram?.q1?.tierB?.monthlyPerInfluencer || 0) * (budgetData.year1.influencerProgram?.q1?.tierB?.count || 0)) +
       ((budgetData.year1.influencerProgram?.q1?.tierC?.monthlyPerInfluencer || 0) * (budgetData.year1.influencerProgram?.q1?.tierC?.count || 0)) +
       ((budgetData.year1.influencerProgram?.q1?.influencerManager?.monthly || 0) + (budgetData.year1.influencerProgram?.q1?.influencerAssistant?.monthly || 0))) * 3 +
      ((budgetData.year1.compliance?.generalLiability?.annual || 0) / 4) +
      ((budgetData.year1.compliance?.cyberInsurance?.annual || 0) / 4) +
      ((budgetData.year1.compliance?.musicLicensing?.annual || 0) / 4) +
      (budgetData.year1.prizesRewards?.quarterlyGrandPrizes?.quarterly || 0) +
      (musicRoyaltyCosts?.totalByQuarter?.q1 || 0);

    // Similar for other quarters...
    const q2Costs = (fixedMonthly * 3) +
      (budgetData.year1.infrastructure?.awsHosting?.q2 || 0) +
      (budgetData.year1.infrastructure?.agoraStreaming?.q2 || 0) +
      (budgetData.year1.infrastructure?.database?.q2 || 0) +
      (budgetData.year1.infrastructure?.cdn?.q2 || 0) +
      (budgetData.year1.infrastructure?.other?.q2 || 0) +
      (budgetData.year1.marketing?.digitalAds?.q2 || 0) +
      (((budgetData.year1.influencerProgram?.q2?.tierA?.monthlyPerInfluencer || 0) * (budgetData.year1.influencerProgram?.q2?.tierA?.count || 0)) +
       ((budgetData.year1.influencerProgram?.q2?.tierB?.monthlyPerInfluencer || 0) * (budgetData.year1.influencerProgram?.q2?.tierB?.count || 0)) +
       ((budgetData.year1.influencerProgram?.q2?.tierC?.monthlyPerInfluencer || 0) * (budgetData.year1.influencerProgram?.q2?.tierC?.count || 0)) +
       ((budgetData.year1.influencerProgram?.q2?.influencerManager?.monthly || 0) + (budgetData.year1.influencerProgram?.q2?.influencerAssistant?.monthly || 0))) * 3 +
      ((budgetData.year1.compliance?.generalLiability?.annual || 0) / 4) +
      ((budgetData.year1.compliance?.cyberInsurance?.annual || 0) / 4) +
      ((budgetData.year1.compliance?.musicLicensing?.annual || 0) / 4) +
      (budgetData.year1.prizesRewards?.quarterlyGrandPrizes?.quarterly || 0) +
      (musicRoyaltyCosts?.totalByQuarter?.q2 || 0);

    const q3Costs = (fixedMonthly * 3) +
      (budgetData.year1.infrastructure?.awsHosting?.q3 || 0) +
      (budgetData.year1.infrastructure?.agoraStreaming?.q3 || 0) +
      (budgetData.year1.infrastructure?.database?.q3 || 0) +
      (budgetData.year1.infrastructure?.cdn?.q3 || 0) +
      (budgetData.year1.infrastructure?.other?.q3 || 0) +
      (budgetData.year1.marketing?.digitalAds?.q3 || 0) +
      (((budgetData.year1.influencerProgram?.q3?.tierA?.monthlyPerInfluencer || 0) * (budgetData.year1.influencerProgram?.q3?.tierA?.count || 0)) +
       ((budgetData.year1.influencerProgram?.q3?.tierB?.monthlyPerInfluencer || 0) * (budgetData.year1.influencerProgram?.q3?.tierB?.count || 0)) +
       ((budgetData.year1.influencerProgram?.q3?.tierC?.monthlyPerInfluencer || 0) * (budgetData.year1.influencerProgram?.q3?.tierC?.count || 0)) +
       ((budgetData.year1.influencerProgram?.q3?.influencerManager?.monthly || 0) + (budgetData.year1.influencerProgram?.q3?.influencerAssistant?.monthly || 0))) * 3 +
      ((budgetData.year1.compliance?.generalLiability?.annual || 0) / 4) +
      ((budgetData.year1.compliance?.cyberInsurance?.annual || 0) / 4) +
      ((budgetData.year1.compliance?.musicLicensing?.annual || 0) / 4) +
      (budgetData.year1.prizesRewards?.quarterlyGrandPrizes?.quarterly || 0) +
      (musicRoyaltyCosts?.totalByQuarter?.q3 || 0);

    const q4Costs = (fixedMonthly * 3) +
      (budgetData.year1.infrastructure?.awsHosting?.q4 || 0) +
      (budgetData.year1.infrastructure?.agoraStreaming?.q4 || 0) +
      (budgetData.year1.infrastructure?.database?.q4 || 0) +
      (budgetData.year1.infrastructure?.cdn?.q4 || 0) +
      (budgetData.year1.infrastructure?.other?.q4 || 0) +
      (budgetData.year1.marketing?.digitalAds?.q4 || 0) +
      (((budgetData.year1.influencerProgram?.q4?.tierA?.monthlyPerInfluencer || 0) * (budgetData.year1.influencerProgram?.q4?.tierA?.count || 0)) +
       ((budgetData.year1.influencerProgram?.q4?.tierB?.monthlyPerInfluencer || 0) * (budgetData.year1.influencerProgram?.q4?.tierB?.count || 0)) +
       ((budgetData.year1.influencerProgram?.q4?.tierC?.monthlyPerInfluencer || 0) * (budgetData.year1.influencerProgram?.q4?.tierC?.count || 0)) +
       ((budgetData.year1.influencerProgram?.q4?.influencerManager?.monthly || 0) + (budgetData.year1.influencerProgram?.q4?.influencerAssistant?.monthly || 0))) * 3 +
      ((budgetData.year1.compliance?.generalLiability?.annual || 0) / 4) +
      ((budgetData.year1.compliance?.cyberInsurance?.annual || 0) / 4) +
      ((budgetData.year1.compliance?.musicLicensing?.annual || 0) / 4) +
      (budgetData.year1.prizesRewards?.quarterlyGrandPrizes?.quarterly || 0) +
      (budgetData.year1.prizesRewards?.yearEndMegaPrize?.total || 0) +
      (musicRoyaltyCosts?.totalByQuarter?.q4 || 0);

    return { q1: q1Costs, q2: q2Costs, q3: q3Costs, q4: q4Costs };
  };

  const quarterlyCosts = calculateQuarterlyCosts();

  // Ensure revenue data has quarterly totals
  if (!revenueData.totalByQuarter) {
    revenueData.totalByQuarter = {
      q1: (revenueData.tips?.q1 || 0) + (revenueData.subscriptions?.q1 || 0) + (revenueData.collectibles?.q1 || 0) + 
           (revenueData.songUploads?.q1 || 0) + (revenueData.adRevenue?.q1 || 0) + (revenueData.studioCreation?.q1 || 0) + 
           (revenueData.creatorSubscriptions?.q1 || 0),
      q2: (revenueData.tips?.q2 || 0) + (revenueData.subscriptions?.q2 || 0) + (revenueData.collectibles?.q2 || 0) + 
           (revenueData.songUploads?.q2 || 0) + (revenueData.adRevenue?.q2 || 0) + (revenueData.studioCreation?.q2 || 0) + 
           (revenueData.creatorSubscriptions?.q2 || 0),
      q3: (revenueData.tips?.q3 || 0) + (revenueData.subscriptions?.q3 || 0) + (revenueData.collectibles?.q3 || 0) + 
           (revenueData.songUploads?.q3 || 0) + (revenueData.adRevenue?.q3 || 0) + (revenueData.studioCreation?.q3 || 0) + 
           (revenueData.creatorSubscriptions?.q3 || 0),
      q4: (revenueData.tips?.q4 || 0) + (revenueData.subscriptions?.q4 || 0) + (revenueData.collectibles?.q4 || 0) + 
           (revenueData.songUploads?.q4 || 0) + (revenueData.adRevenue?.q4 || 0) + (revenueData.studioCreation?.q4 || 0) + 
           (revenueData.creatorSubscriptions?.q4 || 0)
    };
  }

  // Download as PDF function
  const downloadPDF = async () => {
    const element = document.getElementById('funding-sheet-content');
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 295; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;
    
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    
    pdf.save('SIKI-Funding-Sheet-2024.pdf');
  };

  // Only check authorization for admin view (not public view)
  console.log('Authorization check:', { isPublic, userEmail: user?.email });
  
  if (!isPublic) {
    // Admin view requires specific email
    if (user?.email !== 'chris@siki.io') {
      console.log('Returning null - not authorized for admin view');
      return null;
    }
  }
  // Public view doesn't need user check
  
  console.log('Rendering funding sheet content...');

  return (
    <div className="fundingSheet" id="funding-sheet-content">
      <div className="fundingHeader">
        <h1>Funding Sheet - $5M Budget Plan</h1>
        <button 
          onClick={downloadPDF}
          style={{
            position: 'absolute',
            right: '20px',
            top: '20px',
            padding: '10px 20px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Download PDF
        </button>
        <div className="budgetSummary">
          <div className="summaryCard">
            <span className="label">Total Budget</span>
            <span className="value">{formatCurrency(budgetData.totalBudget)}</span>
          </div>
          <div className="summaryCard">
            <span className="label">Year 1 Allocated</span>
            <span className="value">{formatCurrency(grandTotal)}</span>
          </div>
          <div className="summaryCard">
            <span className="label">Remaining</span>
            <span className="value">{formatCurrency(budgetData.totalBudget - grandTotal)}</span>
          </div>
        </div>
      </div>

      <div className="fundingTables">
        {/* Infrastructure */}
        <div className="categorySection">
          <h2>Infrastructure ({formatPercent(totals.infrastructure, year1Total)}) - Scaling from 50K to 2M Users</h2>
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Q1 (50K)</th>
                <th>Q2 (250K)</th>
                <th>Q3 (1M)</th>
                <th>Q4 (2M)</th>
                <th>Year 1 Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>AWS EC2/ECS</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.awsHosting.q1)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.awsHosting.q2)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.awsHosting.q3)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.awsHosting.q4)}/mo</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.awsHosting.q1 + budgetData.year1.infrastructure.awsHosting.q2 + budgetData.year1.infrastructure.awsHosting.q3 + budgetData.year1.infrastructure.awsHosting.q4) * 3)}</td>
              </tr>
              <tr>
                <td>Agora Streaming</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.agoraStreaming.q1)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.agoraStreaming.q2)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.agoraStreaming.q3)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.agoraStreaming.q4)}/mo</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.agoraStreaming.q1 + budgetData.year1.infrastructure.agoraStreaming.q2 + budgetData.year1.infrastructure.agoraStreaming.q3 + budgetData.year1.infrastructure.agoraStreaming.q4) * 3)}</td>
              </tr>
              <tr>
                <td>Redis Cache</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.redisCache.q1)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.redisCache.q2)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.redisCache.q3)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.redisCache.q4)}/mo</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.redisCache.q1 + budgetData.year1.infrastructure.redisCache.q2 + budgetData.year1.infrastructure.redisCache.q3 + budgetData.year1.infrastructure.redisCache.q4) * 3)}</td>
              </tr>
              <tr>
                <td>MongoDB Atlas</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.mongoDatabase.q1)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.mongoDatabase.q2)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.mongoDatabase.q3)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.mongoDatabase.q4)}/mo</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.mongoDatabase.q1 + budgetData.year1.infrastructure.mongoDatabase.q2 + budgetData.year1.infrastructure.mongoDatabase.q3 + budgetData.year1.infrastructure.mongoDatabase.q4) * 3)}</td>
              </tr>
              <tr>
                <td>CloudFront CDN</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.cdnCloudfront.q1)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.cdnCloudfront.q2)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.cdnCloudfront.q3)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.cdnCloudfront.q4)}/mo</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.cdnCloudfront.q1 + budgetData.year1.infrastructure.cdnCloudfront.q2 + budgetData.year1.infrastructure.cdnCloudfront.q3 + budgetData.year1.infrastructure.cdnCloudfront.q4) * 3)}</td>
              </tr>
              <tr>
                <td>Monitoring</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.monitoring.q1)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.monitoring.q2)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.monitoring.q3)}/mo</td>
                <td>{formatCurrency(budgetData.year1.infrastructure.monitoring.q4)}/mo</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.monitoring.q1 + budgetData.year1.infrastructure.monitoring.q2 + budgetData.year1.infrastructure.monitoring.q3 + budgetData.year1.infrastructure.monitoring.q4) * 3)}</td>
              </tr>
              <tr style={{backgroundColor: '#3a3a3a', fontWeight: 'bold'}}>
                <td>Quarterly Total</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.awsHosting.q1 + budgetData.year1.infrastructure.agoraStreaming.q1 + budgetData.year1.infrastructure.redisCache.q1 + budgetData.year1.infrastructure.mongoDatabase.q1 + budgetData.year1.infrastructure.cdnCloudfront.q1 + budgetData.year1.infrastructure.monitoring.q1) * 3)}</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.awsHosting.q2 + budgetData.year1.infrastructure.agoraStreaming.q2 + budgetData.year1.infrastructure.redisCache.q2 + budgetData.year1.infrastructure.mongoDatabase.q2 + budgetData.year1.infrastructure.cdnCloudfront.q2 + budgetData.year1.infrastructure.monitoring.q2) * 3)}</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.awsHosting.q3 + budgetData.year1.infrastructure.agoraStreaming.q3 + budgetData.year1.infrastructure.redisCache.q3 + budgetData.year1.infrastructure.mongoDatabase.q3 + budgetData.year1.infrastructure.cdnCloudfront.q3 + budgetData.year1.infrastructure.monitoring.q3) * 3)}</td>
                <td>{formatCurrency((budgetData.year1.infrastructure.awsHosting.q4 + budgetData.year1.infrastructure.agoraStreaming.q4 + budgetData.year1.infrastructure.redisCache.q4 + budgetData.year1.infrastructure.mongoDatabase.q4 + budgetData.year1.infrastructure.cdnCloudfront.q4 + budgetData.year1.infrastructure.monitoring.q4) * 3)}</td>
                <td style={{color: '#ffee20'}}>{formatCurrency(totals.infrastructure)}</td>
              </tr>
            </tbody>
          </table>
          
          <div style={{marginTop: '1rem', padding: '1rem', backgroundColor: '#333', borderRadius: '8px'}}>
            <h4 style={{color: '#ffee20', marginBottom: '0.5rem'}}>Year 1 Growth Trajectory:</h4>
            <ul style={{listStyle: 'none', padding: 0, fontSize: '0.9rem', color: '#ccc'}}>
              <li>• <strong>Q1 (50K users)</strong>: $9K/month = $27,000 total</li>
              <li>• <strong>Q2 (250K users)</strong>: $18K/month = $54,000 total</li>
              <li>• <strong>Q3 (1M users)</strong>: $45K/month = $135,000 total</li>
              <li>• <strong>Q4 (2M users)</strong>: $82K/month = $246,000 total</li>
              <li style={{marginTop: '0.5rem', color: '#ffee20', fontWeight: 'bold'}}>
                Total Year 1 Infrastructure: {formatCurrency(totals.infrastructure)} (40x user growth!)
              </li>
            </ul>
            <p style={{marginTop: '1rem', fontSize: '0.85rem', color: '#aaa'}}>
              * Aggressive growth plan: reaching 2M users by end of Year 1
              <br />
              * Infrastructure scales efficiently with user growth
              <br />
              * Q4 run rate positions us well for Year 2 expansion to 10M users
            </p>
          </div>
        </div>

        {/* Leadership Team */}
        <div className="categorySection">
          <h2>Leadership Team ({formatPercent(totals.leadership, year1Total)})</h2>
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Monthly Salary</th>
                <th>Months</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>CEO</td>
                <td>{formatCurrency(budgetData.year1.leadership.ceo.monthly)}</td>
                <td>{budgetData.year1.leadership.ceo.months}</td>
                <td>{formatCurrency(budgetData.year1.leadership.ceo.monthly * budgetData.year1.leadership.ceo.months)}</td>
              </tr>
              <tr>
                <td>COO</td>
                <td>{formatCurrency(budgetData.year1.leadership.coo.monthly)}</td>
                <td>{budgetData.year1.leadership.coo.months}</td>
                <td>{formatCurrency(budgetData.year1.leadership.coo.monthly * budgetData.year1.leadership.coo.months)}</td>
              </tr>
              <tr>
                <td>CFO</td>
                <td>{formatCurrency(budgetData.year1.leadership.cfo.monthly)}</td>
                <td>{budgetData.year1.leadership.cfo.months}</td>
                <td>{formatCurrency(budgetData.year1.leadership.cfo.monthly * budgetData.year1.leadership.cfo.months)}</td>
              </tr>
              <tr>
                <td>CMO</td>
                <td>{formatCurrency(budgetData.year1.leadership.cmo.monthly)}</td>
                <td>{budgetData.year1.leadership.cmo.months}</td>
                <td>{formatCurrency(budgetData.year1.leadership.cmo.monthly * budgetData.year1.leadership.cmo.months)}</td>
              </tr>
              <tr>
                <td>CAO</td>
                <td>{formatCurrency(budgetData.year1.leadership.cao.monthly)}</td>
                <td>{budgetData.year1.leadership.cao.months}</td>
                <td>{formatCurrency(budgetData.year1.leadership.cao.monthly * budgetData.year1.leadership.cao.months)}</td>
              </tr>
              <tr>
                <td>Business Development Lead</td>
                <td>{formatCurrency(budgetData.year1.leadership.businessDev.monthly)}</td>
                <td>{budgetData.year1.leadership.businessDev.months}</td>
                <td>{formatCurrency(budgetData.year1.leadership.businessDev.monthly * budgetData.year1.leadership.businessDev.months)}</td>
              </tr>
              <tr>
                <td>Copyright Coordinator</td>
                <td>{formatCurrency(budgetData.year1.leadership.copyrightCoordinator.monthly)}</td>
                <td>{budgetData.year1.leadership.copyrightCoordinator.months}</td>
                <td>{formatCurrency(budgetData.year1.leadership.copyrightCoordinator.monthly * budgetData.year1.leadership.copyrightCoordinator.months)}</td>
              </tr>
              <tr>
                <td>Project Manager</td>
                <td>{formatCurrency(budgetData.year1.leadership.projectManager.monthly)}</td>
                <td>{budgetData.year1.leadership.projectManager.months}</td>
                <td>{formatCurrency(budgetData.year1.leadership.projectManager.monthly * budgetData.year1.leadership.projectManager.months)}</td>
              </tr>
              <tr>
                <td>HR Director</td>
                <td>{formatCurrency(budgetData.year1.leadership.hrDirector.monthly)}</td>
                <td>{budgetData.year1.leadership.hrDirector.months}</td>
                <td>{formatCurrency(budgetData.year1.leadership.hrDirector.monthly * budgetData.year1.leadership.hrDirector.months)}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="3">Subtotal</td>
                <td>{formatCurrency(totals.leadership)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Development Team */}
        <div className="categorySection">
          <h2>Development Team ({formatPercent(totals.development, year1Total)})</h2>
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Monthly Salary</th>
                <th>Months</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Backend Developer</td>
                <td>{formatCurrency(budgetData.year1.development.backendDev.monthly)}</td>
                <td>{budgetData.year1.development.backendDev.months}</td>
                <td>{formatCurrency(budgetData.year1.development.backendDev.monthly * budgetData.year1.development.backendDev.months * (budgetData.year1.development.backendDev.count || 1))}</td>
              </tr>
              <tr>
                <td>Mobile Developer</td>
                <td>{formatCurrency(budgetData.year1.development.mobileDev.monthly)}</td>
                <td>{budgetData.year1.development.mobileDev.months}</td>
                <td>{formatCurrency(budgetData.year1.development.mobileDev.monthly * budgetData.year1.development.mobileDev.months)}</td>
              </tr>
              <tr>
                <td>Frontend Developer</td>
                <td>{formatCurrency(budgetData.year1.development.frontendDev.monthly)}</td>
                <td>{budgetData.year1.development.frontendDev.months}</td>
                <td>{formatCurrency(budgetData.year1.development.frontendDev.monthly * budgetData.year1.development.frontendDev.months)}</td>
              </tr>
              <tr>
                <td>DevOps Engineer</td>
                <td>{formatCurrency(budgetData.year1.development.devOps.monthly)}</td>
                <td>{budgetData.year1.development.devOps.months}</td>
                <td>{formatCurrency(budgetData.year1.development.devOps.monthly * budgetData.year1.development.devOps.months)}</td>
              </tr>
              <tr>
                <td>QA Engineers (2)</td>
                <td>{formatCurrency(budgetData.year1.development.qaDev.monthly)}</td>
                <td>{budgetData.year1.development.qaDev.months}</td>
                <td>{formatCurrency(budgetData.year1.development.qaDev.monthly * budgetData.year1.development.qaDev.months * (budgetData.year1.development.qaDev.count || 1))}</td>
              </tr>
              <tr>
                <td>ML/AI Engineer</td>
                <td>{formatCurrency(budgetData.year1.development.mlEngineer.monthly)}</td>
                <td>{budgetData.year1.development.mlEngineer.months}</td>
                <td>{formatCurrency(budgetData.year1.development.mlEngineer.monthly * budgetData.year1.development.mlEngineer.months)}</td>
              </tr>
              <tr>
                <td>Testers (4)</td>
                <td>{formatCurrency(budgetData.year1.development.tester.monthly)}</td>
                <td>{budgetData.year1.development.tester.months}</td>
                <td>{formatCurrency(budgetData.year1.development.tester.monthly * budgetData.year1.development.tester.months * (budgetData.year1.development.tester.count || 1))}</td>
              </tr>
              <tr>
                <td>Data Engineer</td>
                <td>{formatCurrency(budgetData.year1.development.dataEngineer.monthly)}</td>
                <td>{budgetData.year1.development.dataEngineer.months}</td>
                <td>{formatCurrency(budgetData.year1.development.dataEngineer.monthly * budgetData.year1.development.dataEngineer.months)}</td>
              </tr>
              <tr>
                <td>Security Engineer</td>
                <td>{formatCurrency(budgetData.year1.development.securityEngineer.monthly)}</td>
                <td>{budgetData.year1.development.securityEngineer.months}</td>
                <td>{formatCurrency(budgetData.year1.development.securityEngineer.monthly * budgetData.year1.development.securityEngineer.months)}</td>
              </tr>
              <tr>
                <td>Platform Engineer</td>
                <td>{formatCurrency(budgetData.year1.development.platformEngineer.monthly)}</td>
                <td>{budgetData.year1.development.platformEngineer.months}</td>
                <td>{formatCurrency(budgetData.year1.development.platformEngineer.monthly * budgetData.year1.development.platformEngineer.months)}</td>
              </tr>
              <tr>
                <td>Blockchain Engineer</td>
                <td>{formatCurrency(budgetData.year1.development.blockchainEngineer.monthly)}</td>
                <td>{budgetData.year1.development.blockchainEngineer.months}</td>
                <td>{formatCurrency(budgetData.year1.development.blockchainEngineer.monthly * budgetData.year1.development.blockchainEngineer.months)}</td>
              </tr>
              <tr>
                <td>UX/UI Designer</td>
                <td>{formatCurrency(budgetData.year1.development.uxuiDesigner.monthly)}</td>
                <td>{budgetData.year1.development.uxuiDesigner.months}</td>
                <td>{formatCurrency(budgetData.year1.development.uxuiDesigner.monthly * budgetData.year1.development.uxuiDesigner.months)}</td>
              </tr>
              <tr>
                <td>Graphic Designer</td>
                <td>{formatCurrency(budgetData.year1.development.graphicDesigner.monthly)}</td>
                <td>{budgetData.year1.development.graphicDesigner.months}</td>
                <td>{formatCurrency(budgetData.year1.development.graphicDesigner.monthly * budgetData.year1.development.graphicDesigner.months)}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="3">Subtotal</td>
                <td>{formatCurrency(totals.development)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Sales Team */}
        <div className="categorySection">
          <h2>Sales Team ({formatPercent(totals.sales, year1Total)})</h2>
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Monthly Salary</th>
                <th>Months</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Sales Lead</td>
                <td>{formatCurrency(budgetData.year1.sales.salesLead.monthly)}</td>
                <td>{budgetData.year1.sales.salesLead.months}</td>
                <td>{formatCurrency(budgetData.year1.sales.salesLead.monthly * budgetData.year1.sales.salesLead.months)}</td>
              </tr>
              <tr>
                <td>Sales Representative 1</td>
                <td>{formatCurrency(budgetData.year1.sales.salesRep1.monthly)}</td>
                <td>{budgetData.year1.sales.salesRep1.months}</td>
                <td>{formatCurrency(budgetData.year1.sales.salesRep1.monthly * budgetData.year1.sales.salesRep1.months)}</td>
              </tr>
              <tr>
                <td>Sales Representative 2</td>
                <td>{formatCurrency(budgetData.year1.sales.salesRep2.monthly)}</td>
                <td>{budgetData.year1.sales.salesRep2.months}</td>
                <td>{formatCurrency(budgetData.year1.sales.salesRep2.monthly * budgetData.year1.sales.salesRep2.months)}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="3">Subtotal</td>
                <td>{formatCurrency(totals.sales)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Operations */}
        <div className="categorySection">
          <h2>Operations ({formatPercent(totals.operations, year1Total)})</h2>
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Cost</th>
                <th>Details</th>
                <th>Annual Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Customer Support Team</td>
                <td>{formatCurrency(budgetData.year1.operations.supportTeam.monthly)}</td>
                <td>{budgetData.year1.operations.supportTeam.count} agents × 12 months</td>
                <td>{formatCurrency(budgetData.year1.operations.supportTeam.monthly * budgetData.year1.operations.supportTeam.count * budgetData.year1.operations.supportTeam.months)}</td>
              </tr>
              <tr>
                <td>Legal Services</td>
                <td>{formatCurrency(budgetData.year1.operations.legal.monthly)}</td>
                <td>Monthly × 12</td>
                <td>{formatCurrency(budgetData.year1.operations.legal.monthly * budgetData.year1.operations.legal.months)}</td>
              </tr>
              <tr>
                <td>Accounting Services</td>
                <td>{formatCurrency(budgetData.year1.operations.accounting.monthly)}</td>
                <td>Monthly × 12</td>
                <td>{formatCurrency(budgetData.year1.operations.accounting.monthly * budgetData.year1.operations.accounting.months)}</td>
              </tr>
              <tr>
                <td>Moderators/Clippers</td>
                <td>{formatCurrency(budgetData.year1.operations.moderatorClipper.monthly)}</td>
                <td>{budgetData.year1.operations.moderatorClipper.count} people × 12 months</td>
                <td>{formatCurrency(budgetData.year1.operations.moderatorClipper.monthly * budgetData.year1.operations.moderatorClipper.count * budgetData.year1.operations.moderatorClipper.months)}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="3">Subtotal</td>
                <td>{formatCurrency(totals.operations)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Trust & Safety */}
        <div className="categorySection">
          <h2>Trust & Safety 🛡️</h2>
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Cost</th>
                <th>Details</th>
                <th>Annual Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Trust & Safety Specialist</td>
                <td>{formatCurrency(budgetData.year1.customerSupport.trustSafety.monthly)}</td>
                <td>1 specialist × 12 months</td>
                <td>{formatCurrency(budgetData.year1.customerSupport.trustSafety.monthly * budgetData.year1.customerSupport.trustSafety.count * budgetData.year1.customerSupport.trustSafety.months)}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="3">Subtotal</td>
                <td>{formatCurrency(totals.customerSupport)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Influencer Program - MAJOR FOCUS */}
        <div className="categorySection" style={{border: '2px solid #ffee20'}}>
          <h2>Influencer Program ({formatPercent(totals.influencerProgram, year1Total)}) ⭐</h2>
          <table>
            <thead>
              <tr>
                <th>Quarter</th>
                <th>Tier A</th>
                <th>Tier B</th>
                <th>Tier C</th>
                <th>Staff</th>
                <th>Quarterly Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Q1 (50K users)</td>
                <td>{budgetData.year1.influencerProgram.q1.tierA.count} × $50K = {formatCurrency(budgetData.year1.influencerProgram.q1.tierA.monthlyPerInfluencer * budgetData.year1.influencerProgram.q1.tierA.count * 3)}</td>
                <td>{budgetData.year1.influencerProgram.q1.tierB.count} × $3K = {formatCurrency(budgetData.year1.influencerProgram.q1.tierB.monthlyPerInfluencer * budgetData.year1.influencerProgram.q1.tierB.count * 3)}</td>
                <td>{budgetData.year1.influencerProgram.q1.tierC.count} × $1K = {formatCurrency(budgetData.year1.influencerProgram.q1.tierC.monthlyPerInfluencer * budgetData.year1.influencerProgram.q1.tierC.count * 3)}</td>
                <td>{formatCurrency((budgetData.year1.influencerProgram.q1.influencerManager.monthly + budgetData.year1.influencerProgram.q1.influencerAssistant.monthly) * 3)}</td>
                <td>{formatCurrency(
                  (budgetData.year1.influencerProgram.q1.tierA.monthlyPerInfluencer * budgetData.year1.influencerProgram.q1.tierA.count * 3) +
                  (budgetData.year1.influencerProgram.q1.tierB.monthlyPerInfluencer * budgetData.year1.influencerProgram.q1.tierB.count * 3) +
                  (budgetData.year1.influencerProgram.q1.tierC.monthlyPerInfluencer * budgetData.year1.influencerProgram.q1.tierC.count * 3) +
                  ((budgetData.year1.influencerProgram.q1.influencerManager.monthly + budgetData.year1.influencerProgram.q1.influencerAssistant.monthly) * 3)
                )}</td>
              </tr>
              <tr>
                <td>Q2 (250K users)</td>
                <td>{budgetData.year1.influencerProgram.q2.tierA.count} × $50K = {formatCurrency(budgetData.year1.influencerProgram.q2.tierA.monthlyPerInfluencer * budgetData.year1.influencerProgram.q2.tierA.count * 3)}</td>
                <td>{budgetData.year1.influencerProgram.q2.tierB.count} × $3K = {formatCurrency(budgetData.year1.influencerProgram.q2.tierB.monthlyPerInfluencer * budgetData.year1.influencerProgram.q2.tierB.count * 3)}</td>
                <td>{budgetData.year1.influencerProgram.q2.tierC.count} × $1K = {formatCurrency(budgetData.year1.influencerProgram.q2.tierC.monthlyPerInfluencer * budgetData.year1.influencerProgram.q2.tierC.count * 3)}</td>
                <td>{formatCurrency((budgetData.year1.influencerProgram.q2.influencerManager.monthly + budgetData.year1.influencerProgram.q2.influencerAssistant.monthly) * 3)}</td>
                <td>{formatCurrency(
                  (budgetData.year1.influencerProgram.q2.tierA.monthlyPerInfluencer * budgetData.year1.influencerProgram.q2.tierA.count * 3) +
                  (budgetData.year1.influencerProgram.q2.tierB.monthlyPerInfluencer * budgetData.year1.influencerProgram.q2.tierB.count * 3) +
                  (budgetData.year1.influencerProgram.q2.tierC.monthlyPerInfluencer * budgetData.year1.influencerProgram.q2.tierC.count * 3) +
                  ((budgetData.year1.influencerProgram.q2.influencerManager.monthly + budgetData.year1.influencerProgram.q2.influencerAssistant.monthly) * 3)
                )}</td>
              </tr>
              <tr>
                <td>Q3 (1M users)</td>
                <td>{budgetData.year1.influencerProgram.q3.tierA.count} × $50K = {formatCurrency(budgetData.year1.influencerProgram.q3.tierA.monthlyPerInfluencer * budgetData.year1.influencerProgram.q3.tierA.count * 3)}</td>
                <td>{budgetData.year1.influencerProgram.q3.tierB.count} × $3K = {formatCurrency(budgetData.year1.influencerProgram.q3.tierB.monthlyPerInfluencer * budgetData.year1.influencerProgram.q3.tierB.count * 3)}</td>
                <td>{budgetData.year1.influencerProgram.q3.tierC.count} × $1K = {formatCurrency(budgetData.year1.influencerProgram.q3.tierC.monthlyPerInfluencer * budgetData.year1.influencerProgram.q3.tierC.count * 3)}</td>
                <td>{formatCurrency((budgetData.year1.influencerProgram.q3.influencerManager.monthly + budgetData.year1.influencerProgram.q3.influencerAssistant.monthly) * 3)}</td>
                <td>{formatCurrency(
                  (budgetData.year1.influencerProgram.q3.tierA.monthlyPerInfluencer * budgetData.year1.influencerProgram.q3.tierA.count * 3) +
                  (budgetData.year1.influencerProgram.q3.tierB.monthlyPerInfluencer * budgetData.year1.influencerProgram.q3.tierB.count * 3) +
                  (budgetData.year1.influencerProgram.q3.tierC.monthlyPerInfluencer * budgetData.year1.influencerProgram.q3.tierC.count * 3) +
                  ((budgetData.year1.influencerProgram.q3.influencerManager.monthly + budgetData.year1.influencerProgram.q3.influencerAssistant.monthly) * 3)
                )}</td>
              </tr>
              <tr>
                <td>Q4 (2M users)</td>
                <td>{budgetData.year1.influencerProgram.q4.tierA.count} × $50K = {formatCurrency(budgetData.year1.influencerProgram.q4.tierA.monthlyPerInfluencer * budgetData.year1.influencerProgram.q4.tierA.count * 3)}</td>
                <td>{budgetData.year1.influencerProgram.q4.tierB.count} × $3K = {formatCurrency(budgetData.year1.influencerProgram.q4.tierB.monthlyPerInfluencer * budgetData.year1.influencerProgram.q4.tierB.count * 3)}</td>
                <td>{budgetData.year1.influencerProgram.q4.tierC.count} × $1K = {formatCurrency(budgetData.year1.influencerProgram.q4.tierC.monthlyPerInfluencer * budgetData.year1.influencerProgram.q4.tierC.count * 3)}</td>
                <td>{formatCurrency((budgetData.year1.influencerProgram.q4.influencerManager.monthly + budgetData.year1.influencerProgram.q4.influencerAssistant.monthly) * 3)}</td>
                <td>{formatCurrency(
                  (budgetData.year1.influencerProgram.q4.tierA.monthlyPerInfluencer * budgetData.year1.influencerProgram.q4.tierA.count * 3) +
                  (budgetData.year1.influencerProgram.q4.tierB.monthlyPerInfluencer * budgetData.year1.influencerProgram.q4.tierB.count * 3) +
                  (budgetData.year1.influencerProgram.q4.tierC.monthlyPerInfluencer * budgetData.year1.influencerProgram.q4.tierC.count * 3) +
                  ((budgetData.year1.influencerProgram.q4.influencerManager.monthly + budgetData.year1.influencerProgram.q4.influencerAssistant.monthly) * 3)
                )}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="5">Year 1 Total</td>
                <td>{formatCurrency(totals.influencerProgram)}</td>
              </tr>
            </tbody>
          </table>
          <div style={{marginTop: '1rem', padding: '1rem', backgroundColor: '#333', borderRadius: '8px'}}>
            <h4 style={{color: '#ffee20', marginBottom: '0.5rem'}}>Quarterly Scaling Strategy:</h4>
            <ul style={{listStyle: 'none', padding: 0, fontSize: '0.9rem', color: '#ccc'}}>
              <li>• Q1: Start small with 8 influencers (1A, 2B, 5C) - $168K quarterly</li>
              <li>• Q2: Scale to 12 influencers (1A, 3B, 8C) - $198K quarterly</li>
              <li>• Q3: Major expansion to 17 influencers (2A, 5B, 10C) - $300K quarterly</li>
              <li>• Q4: Full program with 17 influencers maintained - $300K quarterly</li>
              <li style={{marginTop: '0.5rem', borderTop: '1px solid #555', paddingTop: '0.5rem'}}>• Staff costs: $15K/month throughout ($45K per quarter)</li>
              <li style={{marginTop: '0.5rem', color: '#ffee20'}}>Year 1 Total: $1,011,000</li>
              <li style={{marginTop: '0.5rem'}}>• This scaling approach matches user growth and budget availability</li>
            </ul>
          </div>
        </div>

        {/* Prizes & User Rewards - MAJOR FOCUS */}
        <div className="categorySection" style={{border: '2px solid #ffee20'}}>
          <h2>Prizes & User Rewards ({formatPercent(totals.prizesRewards, year1Total)}) 🏆</h2>
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Amount</th>
                <th>Frequency</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Monthly Contest Prizes</td>
                <td>{formatCurrency(budgetData.year1.prizesRewards.monthlyPrizes.monthly)}</td>
                <td>Monthly × 12</td>
                <td>{formatCurrency(budgetData.year1.prizesRewards.monthlyPrizes.monthly * budgetData.year1.prizesRewards.monthlyPrizes.months)}</td>
              </tr>
              <tr>
                <td>Quarterly Grand Prizes</td>
                <td>{formatCurrency(budgetData.year1.prizesRewards.quarterlyGrandPrizes.quarterly)}</td>
                <td>Quarterly × 4</td>
                <td>{formatCurrency(budgetData.year1.prizesRewards.quarterlyGrandPrizes.quarterly * budgetData.year1.prizesRewards.quarterlyGrandPrizes.quarters)}</td>
              </tr>
              <tr>
                <td>Year-End Mega Prize</td>
                <td colSpan="2">Annual Grand Prize</td>
                <td>{formatCurrency(budgetData.year1.prizesRewards.yearEndMegaPrize.total)}</td>
              </tr>
              <tr>
                <td>User Engagement Incentives</td>
                <td>{formatCurrency(budgetData.year1.prizesRewards.userIncentives.monthly)}</td>
                <td>Monthly × 12</td>
                <td>{formatCurrency(budgetData.year1.prizesRewards.userIncentives.monthly * budgetData.year1.prizesRewards.userIncentives.months)}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="3">Subtotal</td>
                <td>{formatCurrency(totals.prizesRewards)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Operations */}
        <div className="categorySection">
          <h2>Operations ({formatPercent(totals.operations, year1Total)})</h2>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Cost</th>
                <th>Details</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Customer Support Team</td>
                <td>{formatCurrency(budgetData.year1.operations.supportTeam.monthly)}</td>
                <td>{budgetData.year1.operations.supportTeam.count} agents × 12 months</td>
                <td>{formatCurrency(budgetData.year1.operations.supportTeam.monthly * budgetData.year1.operations.supportTeam.count * budgetData.year1.operations.supportTeam.months)}</td>
              </tr>
              <tr>
                <td>Legal Services</td>
                <td>{formatCurrency(budgetData.year1.operations.legal.monthly)}</td>
                <td>Monthly × 12</td>
                <td>{formatCurrency(budgetData.year1.operations.legal.monthly * budgetData.year1.operations.legal.months)}</td>
              </tr>
              <tr>
                <td>Accounting Services</td>
                <td>{formatCurrency(budgetData.year1.operations.accounting.monthly)}</td>
                <td>Monthly × 12</td>
                <td>{formatCurrency(budgetData.year1.operations.accounting.monthly * budgetData.year1.operations.accounting.months)}</td>
              </tr>
              <tr>
                <td>Moderators/Clippers</td>
                <td>{formatCurrency(budgetData.year1.operations.moderatorClipper.monthly)}</td>
                <td>{budgetData.year1.operations.moderatorClipper.count} people × 12 months</td>
                <td>{formatCurrency(budgetData.year1.operations.moderatorClipper.monthly * budgetData.year1.operations.moderatorClipper.count * budgetData.year1.operations.moderatorClipper.months)}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="3">Subtotal</td>
                <td>{formatCurrency(totals.operations)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Trust & Safety */}
        <div className="categorySection">
          <h2>Trust & Safety 🛡️</h2>
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Cost</th>
                <th>Details</th>
                <th>Annual Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Trust & Safety Specialist</td>
                <td>{formatCurrency(budgetData.year1.customerSupport.trustSafety.monthly)}</td>
                <td>1 specialist × 12 months</td>
                <td>{formatCurrency(budgetData.year1.customerSupport.trustSafety.monthly * budgetData.year1.customerSupport.trustSafety.count * budgetData.year1.customerSupport.trustSafety.months)}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="3">Subtotal</td>
                <td>{formatCurrency(totals.customerSupport)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Marketing */}
        <div className="categorySection">
          <h2>Marketing 📱</h2>
          <table>
            <thead>
              <tr>
                <th>Channel/Activity</th>
                <th>Q1</th>
                <th>Q2</th>
                <th>Q3</th>
                <th>Q4</th>
                <th>Year Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Digital Advertising</td>
                <td>{formatCurrency(budgetData.year1.marketing.digitalAds.q1)}</td>
                <td>{formatCurrency(budgetData.year1.marketing.digitalAds.q2)}</td>
                <td>{formatCurrency(budgetData.year1.marketing.digitalAds.q3)}</td>
                <td>{formatCurrency(budgetData.year1.marketing.digitalAds.q4)}</td>
                <td>{formatCurrency(budgetData.year1.marketing.digitalAds.q1 + budgetData.year1.marketing.digitalAds.q2 + budgetData.year1.marketing.digitalAds.q3 + budgetData.year1.marketing.digitalAds.q4)}</td>
              </tr>
              <tr>
                <td>PR & Communications</td>
                <td colSpan="4">{formatCurrency(budgetData.year1.marketing.prCommunications.monthly)}/month</td>
                <td>{formatCurrency(budgetData.year1.marketing.prCommunications.monthly * budgetData.year1.marketing.prCommunications.months)}</td>
              </tr>
              <tr>
                <td>Brand Marketing</td>
                <td colSpan="4">{formatCurrency(budgetData.year1.marketing.brandMarketing.quarterly)}/quarter</td>
                <td>{formatCurrency(budgetData.year1.marketing.brandMarketing.quarterly * budgetData.year1.marketing.brandMarketing.quarters)}</td>
              </tr>
              <tr>
                <td>Social Media Manager</td>
                <td colSpan="4">{formatCurrency(budgetData.year1.marketing.socialMediaManager.monthly)}/month</td>
                <td>{formatCurrency(budgetData.year1.marketing.socialMediaManager.monthly * budgetData.year1.marketing.socialMediaManager.months)}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="5">Subtotal</td>
                <td>{formatCurrency(totals.marketing)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Music Royalties */}
        <div className="categorySection">
          <h2>Music Royalties 🎵</h2>
          <table>
            <thead>
              <tr>
                <th>Royalty Type</th>
                <th>Q1 (50K)</th>
                <th>Q2 (250K)</th>
                <th>Q3 (1M)</th>
                <th>Q4 (2M)</th>
                <th>Year 1 Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>On-Demand Streaming</td>
                <td>{formatCurrency(musicRoyaltyCosts.onDemand.q1)}</td>
                <td>{formatCurrency(musicRoyaltyCosts.onDemand.q2)}</td>
                <td>{formatCurrency(musicRoyaltyCosts.onDemand.q3)}</td>
                <td>{formatCurrency(musicRoyaltyCosts.onDemand.q4)}</td>
                <td>{formatCurrency(musicRoyaltyCosts.onDemand.total)}</td>
              </tr>
              <tr>
                <td>Live Stream Royalties</td>
                <td>{formatCurrency(musicRoyaltyCosts.liveStreaming.q1)}</td>
                <td>{formatCurrency(musicRoyaltyCosts.liveStreaming.q2)}</td>
                <td>{formatCurrency(musicRoyaltyCosts.liveStreaming.q3)}</td>
                <td>{formatCurrency(musicRoyaltyCosts.liveStreaming.q4)}</td>
                <td>{formatCurrency(musicRoyaltyCosts.liveStreaming.total)}</td>
              </tr>
              <tr className="totalRow">
                <td>Total Royalty Costs</td>
                <td>{formatCurrency(musicRoyaltyCosts.totalByQuarter.q1)}</td>
                <td>{formatCurrency(musicRoyaltyCosts.totalByQuarter.q2)}</td>
                <td>{formatCurrency(musicRoyaltyCosts.totalByQuarter.q3)}</td>
                <td>{formatCurrency(musicRoyaltyCosts.totalByQuarter.q4)}</td>
                <td>{formatCurrency(totalMusicRoyalties)}</td>
              </tr>
            </tbody>
          </table>
          <div style={{marginTop: '0.5rem', fontSize: '0.85rem', color: '#666'}}>
            <p><strong>Royalty Structure:</strong></p>
            <ul style={{margin: '0.5rem 0', paddingLeft: '1.5rem'}}>
              <li>$0.006 per stream (industry standard)</li>
              <li>Free users: 10 on-demand streams/month (mostly consume via live streams)</li>
              <li>Subscribers: 30 on-demand streams/month (some playlist/favorite access)</li>
              <li>Live streams: Primary consumption method - pay royalties per listener</li>
              <li>Stream sizes: 70% small (10), 20% medium (50), 8% large (500), 2% viral (5K+)</li>
              <li>Weighted avg: ~157 viewers per stream × 15 songs = 2,355 plays per stream</li>
              <li>Live streams: 50/day (Q1) → 2000/day (Q4) as platform grows</li>
            </ul>
          </div>
        </div>


        {/* Compliance & Insurance */}
        <div className="categorySection">
          <h2>Compliance & Insurance 📋</h2>
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Cost</th>
                <th>Frequency</th>
                <th>Annual Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>General Liability Insurance</td>
                <td>{formatCurrency(budgetData.year1.compliance.generalLiability.annual)}</td>
                <td>Annual</td>
                <td>{formatCurrency(budgetData.year1.compliance.generalLiability.annual)}</td>
              </tr>
              <tr>
                <td>Cyber Security Insurance</td>
                <td>{formatCurrency(budgetData.year1.compliance.cyberInsurance.annual)}</td>
                <td>Annual</td>
                <td>{formatCurrency(budgetData.year1.compliance.cyberInsurance.annual)}</td>
              </tr>
              <tr>
                <td>Music Licensing (ASCAP/BMI/SESAC)</td>
                <td>{formatCurrency(budgetData.year1.compliance.musicLicensing.annual)}</td>
                <td>Annual</td>
                <td>{formatCurrency(budgetData.year1.compliance.musicLicensing.annual * budgetData.year1.compliance.musicLicensing.count)}</td>
              </tr>
              <tr>
                <td>Data Privacy Compliance</td>
                <td>{formatCurrency(budgetData.year1.compliance.dataPrivacy.quarterly)}</td>
                <td>Quarterly × 4</td>
                <td>{formatCurrency(budgetData.year1.compliance.dataPrivacy.quarterly * budgetData.year1.compliance.dataPrivacy.quarters)}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="3">Subtotal</td>
                <td>{formatCurrency(totals.compliance)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Office & Facilities */}
        <div className="categorySection">
          <h2>Office & Facilities 🏢</h2>
          <table>
            <thead>
              <tr>
                <th>Expense</th>
                <th>Cost</th>
                <th>Details</th>
                <th>Annual Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Office Rent</td>
                <td>{formatCurrency(budgetData.year1.office.rent.monthly)}</td>
                <td>Monthly × 12</td>
                <td>{formatCurrency(budgetData.year1.office.rent.monthly * budgetData.year1.office.rent.months)}</td>
              </tr>
              <tr>
                <td>Utilities</td>
                <td>{formatCurrency(budgetData.year1.office.utilities.monthly)}</td>
                <td>Monthly × 12</td>
                <td>{formatCurrency(budgetData.year1.office.utilities.monthly * budgetData.year1.office.utilities.months)}</td>
              </tr>
              <tr>
                <td>Equipment & Supplies</td>
                <td>-</td>
                <td>One-time</td>
                <td>{formatCurrency(budgetData.year1.office.equipment.quarterly * budgetData.year1.office.equipment.quarters)}</td>
              </tr>
              <tr>
                <td>Remote Work Stipends</td>
                <td>{formatCurrency(budgetData.year1.office.remoteStipends.monthly)}</td>
                <td>{budgetData.year1.office.remoteStipends.count} employees × 12 months</td>
                <td>{formatCurrency(budgetData.year1.office.remoteStipends.monthly * budgetData.year1.office.remoteStipends.count * budgetData.year1.office.remoteStipends.months)}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="3">Subtotal</td>
                <td>{formatCurrency(totals.office)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Travel & Hotel */}
        <div className="categorySection">
          <h2>Travel & Hotel ✈️</h2>
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Frequency</th>
                <th>Amount</th>
                <th>Annual Cost</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Team All-Hands Meetings</td>
                <td>Quarterly</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.teamMeetings.quarterly)}</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.teamMeetings.quarterly * budgetData.year1.travelAndHotel.teamMeetings.quarters)}</td>
              </tr>
              <tr>
                <td>Client & Partner Meetings</td>
                <td>Monthly</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.clientMeetings.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.clientMeetings.monthly * budgetData.year1.travelAndHotel.clientMeetings.months)}</td>
              </tr>
              <tr>
                <td>Industry Conferences</td>
                <td>Quarterly</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.conferences.quarterly)}</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.conferences.quarterly * budgetData.year1.travelAndHotel.conferences.quarters)}</td>
              </tr>
              <tr>
                <td>Influencer Events</td>
                <td>Quarterly</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.influencerEvents.quarterly)}</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.influencerEvents.quarterly * budgetData.year1.travelAndHotel.influencerEvents.quarters)}</td>
              </tr>
              <tr>
                <td>Investor & Marketing Roadshows</td>
                <td>Quarterly</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.roadshows.quarterly)}</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.roadshows.quarterly * budgetData.year1.travelAndHotel.roadshows.quarters)}</td>
              </tr>
              <tr>
                <td>Executive Team Travel</td>
                <td>Monthly</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.executiveTravel.monthly)}</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.executiveTravel.monthly * budgetData.year1.travelAndHotel.executiveTravel.months)}</td>
              </tr>
              <tr>
                <td>International Market Research</td>
                <td>Quarterly</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.internationalExpansion.quarterly)}</td>
                <td>{formatCurrency(budgetData.year1.travelAndHotel.internationalExpansion.quarterly * budgetData.year1.travelAndHotel.internationalExpansion.quarters)}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="3">Subtotal</td>
                <td>{formatCurrency(totals.travelAndHotel)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Professional Services */}
        <div className="categorySection">
          <h2>Professional Services 💼</h2>
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Cost</th>
                <th>Details</th>
                <th>Annual Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>External Legal Counsel</td>
                <td>{formatCurrency(budgetData.year1.professionalServices.externalLegal.monthly)}</td>
                <td>Monthly retainer × 12</td>
                <td>{formatCurrency(budgetData.year1.professionalServices.externalLegal.monthly * budgetData.year1.professionalServices.externalLegal.months)}</td>
              </tr>
              <tr>
                <td>Financial Audits</td>
                <td>{formatCurrency(budgetData.year1.professionalServices.accounting.quarterly)}</td>
                <td>Quarterly × 4</td>
                <td>{formatCurrency(budgetData.year1.professionalServices.accounting.quarterly * budgetData.year1.professionalServices.accounting.quarters)}</td>
              </tr>
              <tr className="totalRow">
                <td colSpan="3">Subtotal</td>
                <td>{formatCurrency(totals.professionalServices)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Revenue Projections */}
        <div className="categorySection" style={{border: '3px solid #00ff00', backgroundColor: '#003300'}}>
          <h2 style={{color: '#00ff00'}}>Revenue Projections - Scaling from 50K to 2M Users 📈</h2>
          <table>
            <thead>
              <tr>
                <th>Revenue Stream</th>
                <th>Q1 (50K)</th>
                <th>Q2 (250K)</th>
                <th>Q3 (1M)</th>
                <th>Q4 (2M)</th>
                <th>Year 1 Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Tips (25% avg platform cut)</td>
                <td>{formatCurrency(revenueData.tips.q1)}</td>
                <td>{formatCurrency(revenueData.tips.q2)}</td>
                <td>{formatCurrency(revenueData.tips.q3)}</td>
                <td>{formatCurrency(revenueData.tips.q4)}</td>
                <td style={{color: '#00ff00'}}>{formatCurrency(revenueData.tips.total)}</td>
              </tr>
              <tr>
                <td>Platform Subscriptions ($12.99/mo)</td>
                <td>{formatCurrency(revenueData.subscriptions.q1)}</td>
                <td>{formatCurrency(revenueData.subscriptions.q2)}</td>
                <td>{formatCurrency(revenueData.subscriptions.q3)}</td>
                <td>{formatCurrency(revenueData.subscriptions.q4)}</td>
                <td style={{color: '#00ff00'}}>{formatCurrency(revenueData.subscriptions.total)}</td>
              </tr>
              <tr>
                <td>Collectible Sales (3% cut)</td>
                <td>{formatCurrency(revenueData.collectibles.q1)}</td>
                <td>{formatCurrency(revenueData.collectibles.q2)}</td>
                <td>{formatCurrency(revenueData.collectibles.q3)}</td>
                <td>{formatCurrency(revenueData.collectibles.q4)}</td>
                <td style={{color: '#00ff00'}}>{formatCurrency(revenueData.collectibles.total)}</td>
              </tr>
              <tr>
                <td>Song Uploads ($2 each)</td>
                <td>{formatCurrency(revenueData.songUploads.q1)}</td>
                <td>{formatCurrency(revenueData.songUploads.q2)}</td>
                <td>{formatCurrency(revenueData.songUploads.q3)}</td>
                <td>{formatCurrency(revenueData.songUploads.q4)}</td>
                <td style={{color: '#00ff00'}}>{formatCurrency(revenueData.songUploads.total)}</td>
              </tr>
              <tr>
                <td>Ad Revenue (35% platform)</td>
                <td>{formatCurrency(revenueData.adRevenue.q1)}</td>
                <td>{formatCurrency(revenueData.adRevenue.q2)}</td>
                <td>{formatCurrency(revenueData.adRevenue.q3)}</td>
                <td>{formatCurrency(revenueData.adRevenue.q4)}</td>
                <td style={{color: '#00ff00'}}>{formatCurrency(revenueData.adRevenue.total)}</td>
              </tr>
              <tr>
                <td>Studio Creation ($19/mo)</td>
                <td>{formatCurrency(revenueData.studioCreation.q1)}</td>
                <td>{formatCurrency(revenueData.studioCreation.q2)}</td>
                <td>{formatCurrency(revenueData.studioCreation.q3)}</td>
                <td>{formatCurrency(revenueData.studioCreation.q4)}</td>
                <td style={{color: '#00ff00'}}>{formatCurrency(revenueData.studioCreation.total)}</td>
              </tr>
              <tr>
                <td>Creator Subscriptions (30% cut)</td>
                <td>{formatCurrency(revenueData.creatorSubscriptions.q1)}</td>
                <td>{formatCurrency(revenueData.creatorSubscriptions.q2)}</td>
                <td>{formatCurrency(revenueData.creatorSubscriptions.q3)}</td>
                <td>{formatCurrency(revenueData.creatorSubscriptions.q4)}</td>
                <td style={{color: '#00ff00'}}>{formatCurrency(revenueData.creatorSubscriptions.total)}</td>
              </tr>
              <tr style={{backgroundColor: '#004400', fontWeight: 'bold'}}>
                <td>Quarterly Revenue</td>
                <td>{formatCurrency(revenueData.totalByQuarter.q1)}</td>
                <td>{formatCurrency(revenueData.totalByQuarter.q2)}</td>
                <td>{formatCurrency(revenueData.totalByQuarter.q3)}</td>
                <td>{formatCurrency(revenueData.totalByQuarter.q4)}</td>
                <td style={{color: '#00ff00', fontSize: '1.2rem'}}>{formatCurrency(totalRevenue)}</td>
              </tr>
            </tbody>
          </table>
          
          <div style={{marginTop: '1rem', padding: '1rem', backgroundColor: '#004400', borderRadius: '8px'}}>
            <h4 style={{color: '#00ff00', marginBottom: '0.5rem'}}>Revenue Assumptions & Metrics:</h4>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.85rem', color: '#aaffaa'}}>
              <div>
                <strong>Tips & Donations:</strong>
                <ul style={{margin: '0.5rem 0', paddingLeft: '1.5rem'}}>
                  <li>2-5% of users tip creators</li>
                  <li>$8-15/month per active tipper</li>
                  <li>Platform takes 20-30% cut</li>
                </ul>
              </div>
              <div>
                <strong>Subscriptions:</strong>
                <ul style={{margin: '0.5rem 0', paddingLeft: '1.5rem'}}>
                  <li>2% penetration in Q1</li>
                  <li>5% penetration by Q4</li>
                  <li>$12.99/month per subscriber</li>
                </ul>
              </div>
              <div>
                <strong>Creator Economy:</strong>
                <ul style={{margin: '0.5rem 0', paddingLeft: '1.5rem'}}>
                  <li>2% of users are active creators</li>
                  <li>1-2.5 uploads per creator/quarter</li>
                  <li>0.5-1.2% buy collectibles</li>
                </ul>
              </div>
              <div>
                <strong>Monetization:</strong>
                <ul style={{margin: '0.5rem 0', paddingLeft: '1.5rem'}}>
                  <li>Creator subs: $5-20 (30% cut)</li>
                  <li>On-demand: $0.12 ARPU</li>
                  <li>Live streams: $25 CPM</li>
                </ul>
              </div>
            </div>
            <div style={{marginTop: '1rem', padding: '0.5rem', backgroundColor: '#006600', borderRadius: '4px'}}>
              <p style={{margin: 0, color: '#ffffff', fontWeight: 'bold', textAlign: 'center'}}>
                Year 1 Revenue: {formatCurrency(totalRevenue)} | Net Income: {formatCurrency(totalRevenue - grandTotal)}
              </p>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="summarySection">
          <h2>Year 1 Summary</h2>
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Amount</th>
                <th>% of Budget</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Infrastructure</td>
                <td>{formatCurrency(totals.infrastructure)}</td>
                <td>{formatPercent(totals.infrastructure, year1Total)}</td>
              </tr>
              <tr>
                <td>Music Royalties</td>
                <td>{formatCurrency(totals.musicRoyalties)}</td>
                <td>{formatPercent(totals.musicRoyalties, year1Total)}</td>
              </tr>
              <tr>
                <td>Leadership Team</td>
                <td>{formatCurrency(totals.leadership)}</td>
                <td>{formatPercent(totals.leadership, year1Total)}</td>
              </tr>
              <tr>
                <td>Development Team</td>
                <td>{formatCurrency(totals.development)}</td>
                <td>{formatPercent(totals.development, year1Total)}</td>
              </tr>
              <tr>
                <td>Sales Team</td>
                <td>{formatCurrency(totals.sales)}</td>
                <td>{formatPercent(totals.sales, year1Total)}</td>
              </tr>
              <tr style={{backgroundColor: '#ffee2030', fontWeight: 'bold'}}>
                <td>Influencer Program ⭐</td>
                <td>{formatCurrency(totals.influencerProgram)}</td>
                <td>{formatPercent(totals.influencerProgram, year1Total)}</td>
              </tr>
              <tr style={{backgroundColor: '#ffee2030', fontWeight: 'bold'}}>
                <td>Prizes & Rewards 🏆</td>
                <td>{formatCurrency(totals.prizesRewards)}</td>
                <td>{formatPercent(totals.prizesRewards, year1Total)}</td>
              </tr>
              <tr>
                <td>Operations</td>
                <td>{formatCurrency(totals.operations)}</td>
                <td>{formatPercent(totals.operations, year1Total)}</td>
              </tr>
              <tr>
                <td>Trust & Safety</td>
                <td>{formatCurrency(totals.customerSupport)}</td>
                <td>{formatPercent(totals.customerSupport, year1Total)}</td>
              </tr>
              <tr>
                <td>Marketing</td>
                <td>{formatCurrency(totals.marketing)}</td>
                <td>{formatPercent(totals.marketing, year1Total)}</td>
              </tr>
              <tr>
                <td>Compliance & Insurance</td>
                <td>{formatCurrency(totals.compliance)}</td>
                <td>{formatPercent(totals.compliance, year1Total)}</td>
              </tr>
              <tr>
                <td>Office & Facilities</td>
                <td>{formatCurrency(totals.office)}</td>
                <td>{formatPercent(totals.office, year1Total)}</td>
              </tr>
              <tr>
                <td>Travel & Hotel</td>
                <td>{formatCurrency(totals.travelAndHotel)}</td>
                <td>{formatPercent(totals.travelAndHotel, year1Total)}</td>
              </tr>
              <tr>
                <td>Professional Services</td>
                <td>{formatCurrency(totals.professionalServices)}</td>
                <td>{formatPercent(totals.professionalServices, year1Total)}</td>
              </tr>
              <tr className="subtotalRow">
                <td>Subtotal</td>
                <td>{formatCurrency(year1Total)}</td>
                <td>100%</td>
              </tr>
              <tr>
                <td>Contingency (10%)</td>
                <td>{formatCurrency(contingency)}</td>
                <td>{formatPercent(contingency, grandTotal)}</td>
              </tr>
              <tr className="totalRow">
                <td>Year 1 Total</td>
                <td>{formatCurrency(grandTotal)}</td>
                <td>{formatPercent(grandTotal, budgetData.totalBudget)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Key Metrics */}
        <div className="metricsSection">
          <h2>Key Metrics</h2>
          <div className="metricsGrid">
            <div className="metricCard">
              <span className="label">Total Employees</span>
              <span className="value">15</span>
            </div>
            <div className="metricCard">
              <span className="label">Avg Monthly Burn Rate</span>
              <span className="value">{formatCurrency(year1Total / 12)}</span>
            </div>
            <div className="metricCard">
              <span className="label">Runway (months)</span>
              <span className="value">{Math.floor(budgetData.totalBudget / (year1Total / 12))}</span>
            </div>
            <div className="metricCard">
              <span className="label">Cost per User (avg 825k)</span>
              <span className="value">{formatCurrency(year1Total / 825000)}</span>
            </div>
          </div>
        </div>

        {/* Cash Flow Analysis */}
        <div className="categorySection" style={{marginTop: '2rem'}}>
          <h2>Cash Flow Analysis 💰</h2>
          <table style={{fontSize: '0.85rem'}}>
            <thead>
              <tr>
                <th>Quarter</th>
                <th>Starting Cash</th>
                <th>Revenue</th>
                <th>Operating Costs</th>
                <th>Net Burn</th>
                <th>Ending Cash</th>
                <th>Months Runway</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Q1 (50K users)</td>
                <td>{formatCurrency(5000000)}</td>
                <td>{formatCurrency(revenueData.totalByQuarter.q1)}</td>
                <td>{formatCurrency(quarterlyCosts.q1)}</td>
                <td style={{color: revenueData.totalByQuarter.q1 - quarterlyCosts.q1 < 0 ? '#ff6666' : '#66ff66'}}>
                  {formatCurrency(revenueData.totalByQuarter.q1 - quarterlyCosts.q1)}
                </td>
                <td>{formatCurrency(5000000 + (revenueData.totalByQuarter.q1 - quarterlyCosts.q1))}</td>
                <td>{Math.floor((5000000 + (revenueData.totalByQuarter.q1 - quarterlyCosts.q1)) / (quarterlyCosts.q1 / 3))}</td>
              </tr>
              <tr>
                <td>Q2 (250K users)</td>
                <td>{formatCurrency(5000000 + (revenueData.totalByQuarter.q1 - quarterlyCosts.q1))}</td>
                <td>{formatCurrency(revenueData.totalByQuarter.q2)}</td>
                <td>{formatCurrency(quarterlyCosts.q2)}</td>
                <td style={{color: revenueData.totalByQuarter.q2 - quarterlyCosts.q2 < 0 ? '#ff6666' : '#66ff66'}}>
                  {formatCurrency(revenueData.totalByQuarter.q2 - quarterlyCosts.q2)}
                </td>
                <td>{formatCurrency(5000000 + (revenueData.totalByQuarter.q1 - quarterlyCosts.q1) + (revenueData.totalByQuarter.q2 - quarterlyCosts.q2))}</td>
                <td>{Math.floor((5000000 + (revenueData.totalByQuarter.q1 - quarterlyCosts.q1) + (revenueData.totalByQuarter.q2 - quarterlyCosts.q2)) / (quarterlyCosts.q2 / 3))}</td>
              </tr>
              <tr>
                <td>Q3 (1M users)</td>
                <td>{formatCurrency(5000000 + (revenueData.totalByQuarter.q1 - quarterlyCosts.q1) + (revenueData.totalByQuarter.q2 - quarterlyCosts.q2))}</td>
                <td>{formatCurrency(revenueData.totalByQuarter.q3)}</td>
                <td>{formatCurrency(quarterlyCosts.q3)}</td>
                <td style={{color: revenueData.totalByQuarter.q3 - quarterlyCosts.q3 > 0 ? '#66ff66' : '#ff6666'}}>
                  {formatCurrency(revenueData.totalByQuarter.q3 - quarterlyCosts.q3)}
                </td>
                <td>{formatCurrency(5000000 + (revenueData.totalByQuarter.q1 - quarterlyCosts.q1) + (revenueData.totalByQuarter.q2 - quarterlyCosts.q2) + (revenueData.totalByQuarter.q3 - quarterlyCosts.q3))}</td>
                <td>{Math.floor((5000000 + (revenueData.totalByQuarter.q1 - quarterlyCosts.q1) + (revenueData.totalByQuarter.q2 - quarterlyCosts.q2) + (revenueData.totalByQuarter.q3 - quarterlyCosts.q3)) / (quarterlyCosts.q3 / 3))}</td>
              </tr>
              <tr>
                <td>Q4 (2M users)</td>
                <td>{formatCurrency(5000000 + (revenueData.totalByQuarter.q1 - quarterlyCosts.q1) + (revenueData.totalByQuarter.q2 - quarterlyCosts.q2) + (revenueData.totalByQuarter.q3 - quarterlyCosts.q3))}</td>
                <td>{formatCurrency(revenueData.totalByQuarter.q4)}</td>
                <td>{formatCurrency(quarterlyCosts.q4)}</td>
                <td style={{color: revenueData.totalByQuarter.q4 - quarterlyCosts.q4 > 0 ? '#66ff66' : '#ff6666'}}>
                  {formatCurrency(revenueData.totalByQuarter.q4 - quarterlyCosts.q4)}
                </td>
                <td style={{fontWeight: 'bold'}}>
                  {formatCurrency(5000000 + (revenueData.totalByQuarter.q1 - quarterlyCosts.q1) + (revenueData.totalByQuarter.q2 - quarterlyCosts.q2) + (revenueData.totalByQuarter.q3 - quarterlyCosts.q3) + (revenueData.totalByQuarter.q4 - quarterlyCosts.q4))}
                </td>
                <td>∞</td>
              </tr>
            </tbody>
          </table>
          <div style={{marginTop: '1rem', padding: '1rem', backgroundColor: '#1a1a1a', borderRadius: '8px'}}>
            <h4 style={{marginBottom: '0.5rem'}}>Key Insights:</h4>
            <ul style={{listStyle: 'none', padding: 0, fontSize: '0.9rem'}}>
              <li style={{color: revenueData.totalByQuarter.q3 - quarterlyCosts.q3 > 0 ? '#66ff66' : '#ffaa00'}}>
                • Break-even point: {revenueData.totalByQuarter.q3 - quarterlyCosts.q3 > 0 ? 'Q3 with 1M users' : 'Q4 with 2M users'}
              </li>
              <li>• Total Year 1 burn: {formatCurrency(year1Total - totalRevenue)} (after revenue)</li>
              <li>• Cash remaining end of Year 1: {formatCurrency(5000000 - (year1Total - totalRevenue))}</li>
              <li>• Monthly burn rate scales from {formatCurrency(quarterlyCosts.q1 / 3)} (Q1) to {formatCurrency(quarterlyCosts.q4 / 3)} (Q4)</li>
              <li style={{marginTop: '0.5rem', fontWeight: 'bold'}}>
                • Platform becomes cash flow positive in {revenueData.totalByQuarter.q3 - quarterlyCosts.q3 > 0 ? 'Q3' : 'Q4'} and self-sustaining thereafter
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundingSheet;